import React, { useEffect, useState } from "react";
import { Box, Breakpoint, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Fade, Grid, Slide, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Formik, FormikErrors, FormikHelpers, FormikState, FormikTouched } from "formik";
import * as yup from 'yup';
import { MapkitProvider } from "react-mapkit";
import { AppColor, CreatingBeaconViewMode, Fonts } from "../../../../types/Constants";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { deleteBeaconFace, fetchError, getMapkitToken, getUserDetail, makeBeacon, showMessage, updateBeacon, uploadBeaconFace, uploadBeaconImage } from "../../../../redux/actions";
import { Beacon, UserProfile } from '../../../../types/models/Beacon';
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import { convertToUpdateBeacon } from "../../../../utils/utils";
import { Coordinate } from "../../../../types/models/Mapkit";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import AppScrollbar from "../../../../components/AppScrollbar";
import BeaconForm from "./BeaconForm";
import BeaconPreview from "./BeaconPreview";
import BeaconScheduleForm from "./BeaconScheduleForm";
import BeaconPlacementForm from "./BeaconPlacementForm";
import BeaconStyleForm from "./BeaconStyleForm";
import ConfirmModerationDialog, { ModerationActions } from "../../../../components/ConfirmModerationDialog";
import { EmailOutlined } from "@mui/icons-material";
import { useAuthContext } from "../../../authentication/contexts/AuthContext";

export const BeaconDialogModes = ["CREATE", "EDIT", "MOD_ONLY", "READ_ONLY"] as const;

interface BeaconDialogProps {
  maxWidth?: Breakpoint;
  isOpen: boolean;
  onSubmitted: () => void;
  onClose: () => void;
  beacon?: Beacon;
  mode?: typeof BeaconDialogModes[number];
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  // eslint-disable-next-line no-undef
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BeaconDialog: React.FC<BeaconDialogProps> = (prop) => {
  // Util
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
  const auth = useAuthContext();

  // State
  const [viewMode, setViewMode] = useState(CreatingBeaconViewMode.Beacon);
  const [currentImageModalFile, setCurrentImageModalFile] = useState<File | undefined>(undefined);
  const [currentImageFaceFile, setCurrentImageFaceFile] = useState<File | undefined>(undefined);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [deleteFaceImage, setDeleteFaceImage] = useState(false);
  const [openNoImageMessage, setOpenNoImageMessage] = useState<boolean>(false);
  const [currentCoordinate, setCurrentCoordinate] = useState<Coordinate>();
  const [moderationConfirmDialogOpen, setModerationConfirmDialogOpen] = useState<boolean>(false);
  const [moderationAction, setModerationAction] = useState<typeof ModerationActions[number]>("REMOVE");
  const [userProfile, setUserProfile] = useState<UserProfile>();
  const [loadingUserProfile, setLoadingUserProfile] = useState<boolean>(false);
  const [beaconMapProps, setBeaconMapProps] = useState<{tokenOrCallback: string}>({
    tokenOrCallback: ''
  });

  const businessGroupState = useAppSelector(state => state.businessGroup);

  const initialValues: MakeBeacon = {
    name: prop.beacon?.display_name ?? '',
    beacon_group_id: businessGroupState.currentBusinessGroup?.id ?? "",
    description: prop.beacon?.description ?? "",
    category: prop.beacon?.category ?? undefined,
    link: prop.beacon?.link ?? "",
    summary: prop.beacon?.summary ?? undefined,
    image_id: undefined,
    image_ids: [],
    startDate: prop.beacon?.start_time ?? "",
    endDate: prop.beacon?.end_time ?? "",
    latitude: prop.beacon?.latitude ?? undefined,
    longitude: prop.beacon?.longitude ?? undefined,
    model: prop.beacon?.model ?? "ball",
    show_distance: prop.beacon?.show_distance ?? true,
    has_custom_beacon_face: prop.beacon?.has_custom_beacon_face ?? false
  };

  useEffect(() => {
    if(prop.isOpen === true){
      dispatch(
        getMapkitToken((success, token) => {
          if (success && token) {
            setBeaconMapProps({ tokenOrCallback: token });
          }
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.isOpen]);

  useEffect(() => {
    // Trigger to update preview address when edit a beacon
    if (prop.beacon) {
      const coordinate: Coordinate = {
        latitude: prop.beacon.latitude,
        longitude: prop.beacon.longitude
      };
      setCurrentCoordinate(coordinate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.beacon]);

  const handleViewModeChange = (viewType: string) => {
    setViewMode(viewType);
  };

  const getInputForm = (
    values: MakeBeacon,
    errors: FormikErrors<MakeBeacon>,
    handleChange: (e: React.ChangeEvent<any>) => void,
    touched: FormikTouched<MakeBeacon>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>,
    setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void
  ) => {

    switch (viewMode) {
      case CreatingBeaconViewMode.Beacon:
        return <></>;
        // return <BeaconForm
        //   mode={prop.mode}
        //   values={values}
        //   errors={errors}
        //   handleChange={handleChange}
        //   touched={touched}
        //   groups={businessGroupState.businessGroups ?? []}
        // />;
      case CreatingBeaconViewMode.Placement:
        return (
          <MapkitProvider {...beaconMapProps}>
            <BeaconPlacementForm
              mode={prop.mode}
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              setCurrentCoordinate={setCurrentCoordinate} />
          </MapkitProvider>);
      case CreatingBeaconViewMode.Schedule:
        return <BeaconScheduleForm
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          mode={prop.mode}
        />;
      case CreatingBeaconViewMode.Style:
        return <></>;
      default:
        return <Box></Box>;
    }
  };

  const handleSubmit = (values: MakeBeacon, formikHelpers: FormikHelpers<MakeBeacon>) => {
    const beacon_id = prop.beacon?.id;
    const beacon_group_id = businessGroupState.currentBusinessGroup!.id;

    if (!currentImageModalFile) {
      if (prop.mode === "EDIT") {
        // Update beacon without changing the imageModal OR update beacon and delete the imageModal.
        callUpdateBeaconRequest(values, formikHelpers.setSubmitting);
      }
      else {
        // Display confirmation dialog to save without an imageModal
        setOpenNoImageMessage(true);
      }
    }
    else {
      // Upload imageModal + save the beacon
      dispatch(uploadBeaconImage(beacon_group_id, currentImageModalFile, currentImageModalFile?.size, imageDimensions!.width, imageDimensions!.height,(imageId) => {
        if (imageId) {
          values.image_id = imageId;
          if (prop.mode === "EDIT") {
            callUpdateBeaconRequest(values, formikHelpers.setSubmitting);
          }
          else {
            callCreateBeaconRequest(values, formikHelpers.setSubmitting, formikHelpers.resetForm);
          }
        }
        else {
          // Failed to save imageModal
          formikHelpers.setSubmitting(false);
        }
      }));
    }
    if (deleteFaceImage) {
      // Delete the beacon image face
      dispatch(deleteBeaconFace(beacon_id ?? "", beacon_group_id, (result) => {
        if (result) {
          // Successfully delete the image
          console.log("Deleted face image");
        } else {
          // Image didn't exist on beacon
          console.log("Face image failed to delete or didn't exist");
        }
      }));
    }
  };

  const initialiseStates = () => {
    setViewMode(CreatingBeaconViewMode.Beacon);
    setCurrentImageModalFile(undefined);
    setCurrentImageFaceFile(undefined);
    setDeleteFaceImage(false);
    setCurrentCoordinate(undefined);
  };

  const callCreateBeaconRequest = (
    beacon: MakeBeacon,
    setSubmitting: (isSubmitting: boolean) => void,
    resetForm: (nextState?: Partial<FormikState<MakeBeacon>> | undefined) => void) => {
    
    // Call post API to save the beacon
    dispatch(makeBeacon(beacon, async (result) => {
      if (result.success && result.beaconId) {
        const beaconId = result.beaconId;
        const groupId = businessGroupState.currentBusinessGroup?.id;
        if (currentImageFaceFile && groupId) {
          const uploadResult = await new Promise((resolve, reject) => {
            uploadBeaconFaceImage(
              beaconId,
              currentImageFaceFile,
              groupId,
              (result) => {
                if (!result) {
                  dispatch(fetchError("Failed to upload face image"));
                  reject(new Error("Upload failed"));
                } else {
                  resolve(result);
                }
              }
            );
          });
          
          if (!uploadResult) {
            setSubmitting(false);
            return;
          }
        }
        resetForm();
        initialiseStates();
        prop.onSubmitted();
        dispatch(showMessage("The beacon has been created"));

      }
      setSubmitting(false);
    }
    ));
  };

  const callUpdateBeaconRequest = (beacon: MakeBeacon, setSubmitting: (isSubmitting: boolean) => void) => {
    const beaconId = prop.beacon!.id;
    const beaconGroupId = businessGroupState.currentBusinessGroup?.id;
    // Call post API to save the beacon

    if (currentImageFaceFile) {
      uploadBeaconFaceImage(beaconId, currentImageFaceFile, businessGroupState.currentBusinessGroup!.id, (result) => {
        if (!result) {
          dispatch(fetchError("Failed to upload face image"));
        }
      });
    }

    const updateRequest = convertToUpdateBeacon(beacon, beaconId ?? "", beaconGroupId ?? "");
    dispatch(updateBeacon(updateRequest, (result) => {
      if (result) {
        dispatch(showMessage("The beacon has been saved"));

        initialiseStates();
        prop.onSubmitted();

      }
      setSubmitting(false);
    }
    ));
  };

  const uploadBeaconFaceImage = (
    beaconId: string,
    faceFile: File,
    beaconGroupId: string,
    onComplete: (success: boolean) => void
  ) => {
    const formData = new FormData();
    formData.append('file', faceFile, faceFile.name);
  
    dispatch(uploadBeaconFace(beaconId, formData, beaconGroupId, (result) => {
      if (result) {
        setCurrentImageFaceFile(undefined);
        onComplete(true);
      } else {
        onComplete(false);
      }
    }));
  };

  // Attempts to open users default mail client to the beacon owner.
  function emailBeaconOwner() {
    const email = userProfile?.UserAttributes.find(
      (x) => x.Name === "email"
    )?.Value;

    if (email) {
      window.open(`mailto:${email}`, "_blank");
    }
  }

  const validationSchema = yup.object({
    name: yup.string().required("Please enter Beacon name").max(256, "Beacon name must be at most 256 characters"),
    category: yup.string().required("Please select a category"),
    link: yup.string().matches(/^https?:\/\/.*$/, 'http://example.com'),
    startDate: yup.date().default(() => new Date()),
    endDate: yup.date().required("Please set an end time").when("startDate", (startDate, schema) => startDate && schema.min(startDate, "The beacon must end after it starts")),
    latitude: yup.number().required("Please select a location"),
    longitude: yup.number().required("Please select a location"),
    model: yup.string().required("Please select a model type")
  });

  const errorIconIfInvalid = (viewMode: string, errors: FormikErrors<MakeBeacon>, touched: FormikTouched<MakeBeacon>) => {
    let error: boolean | undefined = false;

    switch (viewMode) {
      case CreatingBeaconViewMode.Beacon:
        error = ((errors.name ? true : false) && touched.name) ||
          ((errors.description ? true : false) && touched.description) ||
          ((errors.category ? true : false) && touched.category) ||
          ((errors.link ? true : false) && touched.link) ||
          ((errors.beacon_group_id ? true : false) && touched.beacon_group_id);
        break;
      case CreatingBeaconViewMode.Placement:
        error = ((errors.longitude ? true : false) && touched.longitude) ||
          ((errors.latitude ? true : false) && touched.latitude);
        break;
      case CreatingBeaconViewMode.Schedule:
        error = ((errors.startDate ? true : false) && touched.startDate) ||
          ((errors.endDate ? true : false) && touched.endDate);
        break;
      case CreatingBeaconViewMode.Style:
        error = ((errors.model ? true : false) && touched.model);
        break;
    }
    if (error) {
      return <ErrorOutlineIcon sx={{ marginLeft: 1, color: "red", fontSize: 20 }} />;
    }
    else {
      return <></>;
    }
  };

  return (
    <Formik
      enableReinitialize
      validateOnChange={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ values, errors, touched, isSubmitting, handleChange, submitForm, resetForm, setFieldValue, setFieldTouched, setSubmitting }) => {

        return (
          <>
            {/* Moderation-only dialog. */}
            {prop.beacon && (
              <ConfirmModerationDialog
                action={moderationAction}
                beacon={prop.beacon}
                open={moderationConfirmDialogOpen}
                toggleOpen={() =>
                  setModerationConfirmDialogOpen(!moderationConfirmDialogOpen)
                }
              />
            )}

            {/* Rest of the dialog. */}
            <Dialog
              sx={{
                "& .MuiDialog-paper": {
                  width: fullscreen ? "100%" : "70%",
                  maxWidth: "1536px",
                  backgroundColor: "white",
                },
                "& .MuiDialogContent-root": {
                  overflowY: "hidden",
                  paddingLeft: 0,
                  paddingRight: 0,
                },
                // minWidth: "800px",
              }}
              TransitionComponent={Transition}
              open={prop.isOpen}
              fullScreen={fullscreen}
            >
              <DialogTitle sx={{ paddingY: 2, paddingX: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  {/* TODO: Titling is a bit redundant, collapse to 1 typog el. */}
                  {(prop.mode === "MOD_ONLY" || prop.mode === "READ_ONLY" )&& (
                    <Typography
                      sx={{ fontWeight: Fonts.SEMI_BOLD, fontSize: 20 }}
                    >
                      View
                    </Typography>
                  )}
                  {(prop.mode !== "MOD_ONLY" && prop.mode !== "READ_ONLY") && (
                    <Typography
                      sx={{ fontWeight: Fonts.SEMI_BOLD, fontSize: 20 }}
                    >
                      {prop.beacon && prop.beacon.display_name.length > 0
                        ? "Edit"
                        : "Create"}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {/* Chip showing email of beacon owner. */}

                    {/* Save - General - Only visible outside of mod_only. */}
                    {(prop.mode !== "MOD_ONLY" && prop.mode !== "READ_ONLY") && (
                      <Button
                        sx={{ marginRight: 2 }}
                        variant="contained"
                        disabled={isSubmitting}
                        onClick={submitForm}
                      >
                        Save
                      </Button>
                    )}
                    {/* Remove - Moderation - Requires MOD_ONLY */}
                    {prop.mode === "MOD_ONLY" && (
                      <Button
                        sx={{ marginRight: 2 }}
                        variant="contained"
                        disabled={isSubmitting}
                        onClick={() => {
                          setModerationAction("REMOVE");
                          setModerationConfirmDialogOpen(true);
                        }}
                      >
                        Remove Beacon
                      </Button>
                    )}
                    {/* Ban - Moderation - Requires MOD_ONLY*/}
                    {prop.mode === "MOD_ONLY" && (
                      <Button
                        sx={{ marginRight: 2 }}
                        variant="contained"
                        disabled={isSubmitting}
                        onClick={() => {
                          setModerationAction("BAN");
                          setModerationConfirmDialogOpen(true);
                        }}
                      >
                        Ban User
                      </Button>
                    )}
                    {/* Close / Cancel - Mode-agnostic. */}
                    <Button
                      variant="outlined"
                      sx={{ marginRight: 2 }}
                      onClick={() => {
                        resetForm();
                        initialiseStates();
                        prop.onClose();
                      }}
                    >
                      {prop.mode === "MOD_ONLY" ? "Close" : "Cancel"}
                    </Button>
                  </Box>
                </Box>
              </DialogTitle>
              <Box>
              <DialogContent dividers={true} sx={{ paddingY: 2 }}>
                <Box sx={{ height: "100%" }}>
                  <AppScrollbar forceVisible="y" autoHide={false}>
                    <Grid container>
                      {/* Beacon Forms */}
                      <Grid item xs={fullscreen ? 12 : 6} sx={{ px: 2 }}>
                        <Box>
                          <ToggleButtonGroup
                            exclusive
                            color="secondary"
                            value={viewMode}
                            onChange={(event, newValue) => {
                              if (newValue) handleViewModeChange(newValue);
                            }}
                            aria-label="Platform"
                            sx={{
                              zIndex: -1,
                              flexWrap: "nowrap",
                              "& .MuiToggleButton-root": {
                                backgroundColor: AppColor.skeletonBase,
                                border: "none",
                                textTransform: "none",
                                padding: "2px",
                                minWidth: "106px",
                              },
                              "& .Mui-selected": {
                                color: AppColor.baseOrange,
                                backgroundColor: AppColor.orangeBackground
                              }
                            }}
                          >
                            <ToggleButton
                              tabIndex={0}
                              value={CreatingBeaconViewMode.Beacon}
                            >
                              Beacon
                              {errorIconIfInvalid(
                                CreatingBeaconViewMode.Beacon,
                                errors,
                                touched
                              )}
                            </ToggleButton>
                            <ToggleButton
                              tabIndex={1}
                              value={CreatingBeaconViewMode.Placement}
                            >
                              Placement
                              {errorIconIfInvalid(
                                CreatingBeaconViewMode.Placement,
                                errors,
                                touched
                              )}
                            </ToggleButton>
                            <ToggleButton
                              tabIndex={2}
                              value={CreatingBeaconViewMode.Schedule}
                            >
                              Schedule
                              {errorIconIfInvalid(
                                CreatingBeaconViewMode.Schedule,
                                errors,
                                touched
                              )}
                            </ToggleButton>
                            <ToggleButton
                              disabled={prop.mode === "MOD_ONLY"}
                              tabIndex={3}
                              value={CreatingBeaconViewMode.Style}
                            >
                              Style
                              {errorIconIfInvalid(
                                CreatingBeaconViewMode.Style,
                                errors,
                                touched
                              )}
                            </ToggleButton>
                          </ToggleButtonGroup>
                          <Box sx={{ paddingTop: 2 }}>
                            <Box>
                              {getInputForm(
                                values,
                                errors,
                                handleChange,
                                touched,
                                setFieldValue,
                                setFieldTouched
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                      {/* Preview Screen */}
                      <Grid item xs={fullscreen ? 12 : 6} sx={{ minWidth: "120px", zIndex: 999, height: "80vh", px: 2 }}>
                        <MapkitProvider {...beaconMapProps}>
                          <BeaconPreview
                            name={values.name}
                            beaconId={prop.beacon?.id}
                            category={values.category}
                            description={values.description}
                            imageModal={currentImageModalFile}
                            imageFace={currentImageFaceFile}
                            imageId={values.image_id}
                            coordinate={currentCoordinate}
                            startDate={values.startDate}
                            hasCustomBeaconFace={prop.beacon?.has_custom_beacon_face}
                            lastModified={prop.beacon?.last_modified}
                          ></BeaconPreview>
                        </MapkitProvider>
                      </Grid>
                    </Grid>
                  </AppScrollbar>
                </Box>
              </DialogContent>
              </Box>
              <AppConfirmDialog
                open={openNoImageMessage}
                onConfirm={() => {
                  setOpenNoImageMessage(false);
                  callCreateBeaconRequest(values, setSubmitting, resetForm);
                }}
                onDeny={() => {
                  setSubmitting(false);
                  setOpenNoImageMessage(false);
                }}
                title="Are you sure you want to save the beacon without an image modal?"
                dialogTitle="Beacon"
                content=""
              />
              {isSubmitting && (
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress
                    size="3rem"
                    sx={{ color: AppColor.baseOrange }}
                  />
                </Box>
              )}
            </Dialog>
          </>
        );
      }
      }
    </Formik >
  );
};

export default BeaconDialog;
